import { useCallback } from "react";
import { api } from "../utils/apiClient";
import { LANGUAGE_STORAGE_KEY } from "../i18n/constants";

// Services where T&C/disclaimers must be accepted on every request,
// regardless of previous acceptances (BSC legal requirement).
const PER_REQUEST_ACCEPTANCE_PATHS = new Set(["/donate-dataset"]);

function isPerRequestAcceptancePath(path) {
  const normalized = String(path || "").trim();
  return PER_REQUEST_ACCEPTANCE_PATHS.has(
    normalized.startsWith("/") ? normalized : `/${normalized}`,
  );
}

// Service legal-document check used by LegalAcceptanceProvider.
// awaitAcceptance=false: gate check (cached per path); true: per-request acceptance prompt.
export function useServiceAcceptanceCheck({
  user,
  authLoading,
  fetchDocumentContent,
  queuePendingDocument,
  acceptedDocumentsRef,
  serviceAccessMapRef,
  serviceCheckInFlightRef,
  acceptanceResolverRef,
  setServiceCheckLoading,
  setLoadError,
  setAcceptedDocuments,
  setAcceptanceModalOpen,
}) {
  const runCheck = useRunCheck({
    fetchDocumentContent,
    queuePendingDocument,
    acceptedDocumentsRef,
    serviceAccessMapRef,
    acceptanceResolverRef,
    setLoadError,
    setAcceptedDocuments,
    setAcceptanceModalOpen,
  });

  return useCallback(
    async (servicePath, awaitAcceptance) => {
      if (awaitAcceptance) {
        if (!user || user.role === "sysAdmin") {
          return true;
        }
      } else {
        if (!user || authLoading) {
          return false;
        }

        if (user.role === "sysAdmin") {
          return true;
        }

        if (!servicePath) {
          return true;
        }

        // Per-request services open freely; acceptance is required at submit time.
        if (isPerRequestAcceptancePath(servicePath)) {
          return true;
        }

        // Re-check on every entry so a document published mid-session prompts.
        // The service stays hidden until the check returns, so no 403s fire.
        const previouslyAllowed = serviceAccessMapRef.current[servicePath] === true;

        // Concurrent gate checks share one result; granting access early
        // would render the service behind the modal and trigger 403s.
        const inFlight = serviceCheckInFlightRef.current.get(servicePath);
        if (inFlight) {
          return inFlight;
        }

        // Skip the full-page spinner for services the user already entered.
        if (!previouslyAllowed) setServiceCheckLoading(true);
        setLoadError(null);
        const check = runCheck(servicePath, false).finally(() => {
          serviceCheckInFlightRef.current.delete(servicePath);
          setServiceCheckLoading(false);
        });
        serviceCheckInFlightRef.current.set(servicePath, check);
        return check;
      }

      return runCheck(servicePath, true);
    },
    [
      authLoading,
      runCheck,
      serviceAccessMapRef,
      serviceCheckInFlightRef,
      setLoadError,
      setServiceCheckLoading,
      user,
    ],
  );
}

function useRunCheck({
  fetchDocumentContent,
  queuePendingDocument,
  acceptedDocumentsRef,
  serviceAccessMapRef,
  acceptanceResolverRef,
  setLoadError,
  setAcceptedDocuments,
  setAcceptanceModalOpen,
}) {
  return useCallback(
    async (servicePath, awaitAcceptance) => {
      const preferredLanguage =
        typeof window !== "undefined"
          ? window.localStorage.getItem(LANGUAGE_STORAGE_KEY) || "en"
          : "en";

      try {
        const { data } = await api.get("/api/services/legal-document/check-by-path", {
          params: { servicePath, language: preferredLanguage },
        });

        if (!data?.ok) {
          if (!awaitAcceptance) {
            serviceAccessMapRef.current[servicePath] = false;
            return false;
          }
          return true;
        }

        if (!data.requiresAcceptance || !data.document) {
          if (!awaitAcceptance) {
            serviceAccessMapRef.current[servicePath] = true;
          }
          return true;
        }

        if (!awaitAcceptance) {
          if (data.accepted) {
            // Keep the same Set when unchanged: ServiceLegalRoute re-checks on identity change.
            setAcceptedDocuments((prev) =>
              prev.has(data.document.id) ? prev : new Set([...prev, data.document.id]),
            );
            serviceAccessMapRef.current[servicePath] = true;
            return true;
          }

          const alreadyAccepted = acceptedDocumentsRef.current.has(data.document.id);
          if (alreadyAccepted) {
            serviceAccessMapRef.current[servicePath] = true;
            return true;
          }
        }

        const content = data.document.content || (await fetchDocumentContent(data.document.id));
        queuePendingDocument({ ...data.document, content });
        setLoadError(null);
        setAcceptanceModalOpen(true);

        if (!awaitAcceptance) {
          serviceAccessMapRef.current[servicePath] = false;
          return false;
        }

        return new Promise((resolve) => {
          acceptanceResolverRef.current?.(false); // settle a superseded request
          acceptanceResolverRef.current = resolve;
        });
      } catch (_error) {
        setLoadError("Failed to load the required service legal document.");
        setAcceptanceModalOpen(true);
        if (!awaitAcceptance) {
          serviceAccessMapRef.current[servicePath] = false;
          return false;
        }
        return false;
      }
    },
    [
      acceptanceResolverRef,
      acceptedDocumentsRef,
      fetchDocumentContent,
      queuePendingDocument,
      serviceAccessMapRef,
      setAcceptanceModalOpen,
      setAcceptedDocuments,
      setLoadError,
    ],
  );
}
